import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'

export type TaxonomyItem = {
  id: string
  name: string
  status: 'pending' | 'official'
  submission_count: number
}

type Props = {
  title: string
  description: string
  resourcePath: string
  paramKey: string
  baseConstants: string[]
  pending: TaxonomyItem[]
  official: TaxonomyItem[]
  addLabel: string
  addPlaceholder: string
}

export default function TaxonomyManager({
  title,
  description,
  resourcePath,
  paramKey,
  baseConstants,
  pending,
  official,
  addLabel,
  addPlaceholder,
}: Props) {
  const [newName, setNewName] = useState('')

  function addOfficial() {
    const name = newName.trim()
    if (!name) return
    router.post(
      resourcePath,
      { [paramKey]: { name } },
      { onSuccess: () => setNewName('') }
    )
  }

  function promote(item: TaxonomyItem) {
    router.patch(`${resourcePath}/${item.id}`, { [paramKey]: { status: 'official' } })
  }

  function remove(item: TaxonomyItem, confirmMessage: string) {
    if (!confirm(confirmMessage)) return
    router.delete(`${resourcePath}/${item.id}`)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight font-serif">{title}</h1>
        <p className="mt-1 text-base text-muted-foreground">{description}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{addLabel}</CardTitle>
          <CardDescription>
            Adds a new option directly to the official list. To approve user submissions, use the pending
            section below instead.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addOfficial()
                }
              }}
              placeholder={addPlaceholder}
            />
            <Button type="button" onClick={addOfficial} disabled={!newName.trim()}>
              Add
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Pending submissions</CardTitle>
          <CardDescription>
            Custom values submitted by users and organizations, sorted by how many people submitted them.
            Promoting makes them appear as suggestions to everyone.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pending.length === 0 ? (
            <p className="text-sm text-muted-foreground">No pending submissions.</p>
          ) : (
            <ul className="divide-y divide-black/5">
              {pending.map(item => (
                <li key={item.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="flex items-center gap-3">
                    <span className="font-medium">{item.name}</span>
                    <Badge variant="secondary">
                      {item.submission_count} submission{item.submission_count === 1 ? '' : 's'}
                    </Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" onClick={() => promote(item)}>
                      Promote
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => remove(item, `Reject "${item.name}"? Existing selections will keep the value, but it won't appear as a suggestion.`)}
                    >
                      Reject
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Official list</CardTitle>
          <CardDescription>
            The complete set of suggestions shown to users. Built-in values are baked into the app; admin-added
            and admin-promoted values can be removed here.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="mb-2 text-sm font-medium text-muted-foreground">Built-in (cannot be removed)</p>
            <div className="flex flex-wrap gap-2">
              {baseConstants.map(name => (
                <Badge key={name} variant="outline">{name}</Badge>
              ))}
            </div>
          </div>
          <div>
            <p className="mb-2 text-sm font-medium text-muted-foreground">Added by admins</p>
            {official.length === 0 ? (
              <p className="text-sm text-muted-foreground">None yet.</p>
            ) : (
              <ul className="divide-y divide-black/5">
                {official.map(item => (
                  <li key={item.id} className="flex items-center justify-between gap-3 py-2">
                    <span>{item.name}</span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => remove(item, `Remove "${item.name}" from the official list? It will no longer appear as a suggestion.`)}
                    >
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
